import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { PercentageIcon } from 'components/icons/Percentage'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import { RubricScoreWidgetBox } from 'components/breakout/RubricScoreWidget'
import type { RubricType } from '@breakoutlearning/firebase-repository/types'
import { useCallback } from 'react'
import { useTranslationTyped } from 'i18n/i18n'
import {
  isScoreMappingDescending,
  type ScaleLevel,
} from './customRubricsConfig'

/**
 * Controlled editor for one scale's score→percentage mapping. One row per score
 * level (coloured badge + label + percentage input); values must stay
 * descending. The parent owns the per-scale values so tab switching preserves
 * each scale's edits.
 */
export function ConfigureScoreMapping({
  levels,
  rubricType,
  values,
  disabled,
  onChange,
}: {
  levels: ScaleLevel[]
  /** Representative rubric type for this scale; drives the score badge colour. */
  rubricType: RubricType
  values: Record<string, number>
  disabled?: boolean
  onChange: (next: Record<string, number>) => void
}) {
  const { tt } = useTranslationTyped()

  const handleChange = useCallback(
    (score: number, raw: string) => {
      const parsed = raw ? Number.parseInt(raw) : 0
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) return
      onChange({ ...values, [String(score)]: parsed })
    },
    [onChange, values]
  )

  const valid = isScoreMappingDescending(values, levels)

  // The top (highest) score level is read-only in the UI: mapping the best
  // possible score below full credit is almost never intended. It still displays
  // the stored value (so a manually-edited mapping renders accurately) — it just
  // can't be changed. This is a UX-only lock, not enforced at the schema or
  // validation layer.
  const topScore = levels.reduce((max, level) => Math.max(max, level.score), 0)

  return (
    <div className="flex w-full flex-col gap-2">
      {levels.map((level) => {
        const isTopLevel = level.score === topScore
        const label = tt.instructor_library[level.labelKey]()
        return (
          <div
            key={level.score}
            className="flex flex-row items-center justify-between gap-2"
          >
            <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
              <RubricScoreWidgetBox
                rubricType={rubricType}
                rubricText={label}
                rubricResult={level.score}
              />
              <strong className="text-label-medium">{label}</strong>
            </div>
            <BreakoutTextInput
              testId={`score-mapping-input-${level.score}`}
              kind="secondary"
              variant="compact"
              className="w-15 shrink-0"
              value={String(values[String(level.score)] ?? 0)}
              disabled={disabled}
              preventEditingValue={isTopLevel}
              onChange={(e) => handleChange(level.score, e.target.value)}
              type="number"
              min={0}
              max={100}
              TrailingIcon={PercentageIcon}
              iconSize={10}
              iconClassName={`text-on-surface-var !right-[15px]${
                isTopLevel ? ' pointer-events-none' : ''
              }`}
              inputClassName="text-right pr-[26px]! pl-2 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </div>
        )
      })}
      {!valid && (
        <div
          data-testid="score-mapping-error"
          className="flex flex-row items-center gap-1 text-label-medium text-core-error"
        >
          <TriangleAlertIcon size={12.5} />
          <span>
            {tt.instructor_library.custom_rubrics_score_mapping_error()}
          </span>
        </div>
      )}
    </div>
  )
}
